import { z } from "zod";
import { PlatformTypeZ } from "../domain/platform";

/**
 * Bot self-enrollment + sync API.
 *
 * Two endpoints:
 *   POST /webhooks/bots/register — bootstrap (shared EA_WEBHOOK_SECRET).
 *     One-shot per botName, returns a per-bot token. 409 if botName
 *     already exists.
 *
 *   POST /webhooks/bots/sync — per-bot auth (X-PFH-Bot header + per-bot
 *     token HMAC). Idempotent, refreshes `last_seen` and returns the
 *     latest accounts roster + fleet config. Bot calls this on startup
 *     after register, and every 5 min thereafter.
 *
 * Wire format identical to other webhooks:
 *   X-PFH-Timestamp: <unix ms>
 *   X-PFH-Signature: hex(hmac_sha256(secret, ts + "." + raw_body))
 *   X-PFH-Bot:       <botName>            ← only on /sync (and on
 *                                            position/commands/news once
 *                                            the bot has a token)
 */

// ─── Capabilities ────────────────────────────────────────────────
export const BotCapabilitiesZ = z.object({
  drivers: z.array(PlatformTypeZ).default([]),
});
export type BotCapabilities = z.infer<typeof BotCapabilitiesZ>;

// ─── Fleet config (server → bot) ─────────────────────────────────
export const FleetConfigZ = z.object({
  /** Full WebSocket URL the bot should connect to. */
  wsUrl: z.string().url(),
  /** Sync cadence the bot should use (ms). */
  syncIntervalMs: z.number().int().positive(),
});
export type FleetConfig = z.infer<typeof FleetConfigZ>;

// ─── Account row (server → bot) ──────────────────────────────────
// Matches the row shape returned by the existing GET /webhooks/accounts.
// Kept loose (passthrough) so adding columns server-side doesn't require
// a coordinated bot redeploy.
export const BotAccountRowZ = z
  .object({
    id: z.string(),
    firm_id: z.string(),
    profile_key: z.string().nullable().optional(),
    platform_type: z.string(),
    adapter: z.string(),
    role: z.string(),
    phase: z.string(),
    login: z.string().nullable(),
    password: z.string().nullable(),
    broker_server: z.string().nullable(),
    storage_state: z.string().nullable(),
    login_url: z.string().nullable(),
    /** Match-Trader dashboards host many accounts per login; this is
     *  the label/number the driver matches against on the account list
     *  screen. NULL for MT5 / TradeLocker where the creds already
     *  pick the account. Optional (not just nullable) so pre-0019
     *  server payloads that omit the field still parse. */
    account_label: z.string().nullable().optional(),
    /** JSON-serialized preferred symbol list for this account. Sent as
     *  a string from SQL so older bot builds keep parsing; the bot
     *  runtime deserializes it into AccountConfig.preferredSymbols. */
    preferred_symbols: z.string().nullable().optional(),
    require_sl: z.number().nullable(),
    require_tp: z.number().nullable(),
    default_comment: z.string().nullable(),
  })
  .passthrough();
export type BotAccountRow = z.infer<typeof BotAccountRowZ>;

// ─── Register ────────────────────────────────────────────────────
export const RegisterReqZ = z.object({
  botName: z.string().min(1).max(64).regex(/^[A-Za-z0-9._-]+$/),
  hostname: z.string().max(255).optional(),
  version: z.string().max(64).optional(),
  capabilities: BotCapabilitiesZ.optional(),
});
export type RegisterReq = z.infer<typeof RegisterReqZ>;

export const RegisterResZ = z.object({
  ok: z.literal(true),
  botName: z.string(),
  /** Persisted to bot-id.json; used as HMAC secret for all subsequent calls. */
  token: z.string().length(64),
  serverTime: z.number().int(),
  accounts: z.array(BotAccountRowZ),
  fleetConfig: FleetConfigZ,
});
export type RegisterRes = z.infer<typeof RegisterResZ>;

// ─── Sync ────────────────────────────────────────────────────────
export const SyncReqZ = z.object({
  botName: z.string().min(1).max(64),
  hostname: z.string().max(255).optional(),
  version: z.string().max(64).optional(),
});
export type SyncReq = z.infer<typeof SyncReqZ>;

export const SyncResZ = z.object({
  ok: z.literal(true),
  botName: z.string(),
  serverTime: z.number().int(),
  accounts: z.array(BotAccountRowZ),
  fleetConfig: FleetConfigZ,
});
export type SyncRes = z.infer<typeof SyncResZ>;
